/**
 * Format user name for display
 * @param {string} name - User name
 * @returns {string} - Formatted name
 */
export const formatUserName = (name) => {
  if (!name) return 'User';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

/**
 * Get user initials for avatar
 * @param {string} name - User name
 * @returns {string} - User initials
 */
export const getUserInitials = (name) => {
  if (!name) return 'U';
  return name.charAt(0).toUpperCase();
};

/**
 * Format age with proper text
 * @param {number} age - User age
 * @returns {string} - Formatted age text
 */
export const formatAge = (age) => {
  if (!age) return 'Unknown age';
  return `${age} years`;
};

/**
 * Format gender with proper text
 * @param {string} gender - User gender
 * @returns {string} - Formatted gender text
 */
export const formatGender = (gender) => {
  if (!gender) return 'Unknown';
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
};

/**
 * Generate random progress value for simulation
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} - Random progress value
 */
export const generateRandomProgress = (min = 0, max = 100) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Simulate API delay
 * @param {number} ms - Delay in milliseconds
 * @returns {Promise} - Promise that resolves after delay
 */
export const delay = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Validate user data
 * @param {Object} userData - User data object
 * @returns {boolean} - Whether data is valid
 */
export const validateUserData = (userData) => {
  if (!userData) return false;
  
  // Check for required fields (name, age, gender)
  const hasRequiredFields = !!(userData.name && userData.age && userData.gender);
  
  // Additional validation for patient data from current_patient API
  const hasPatientFields = !!(userData.patientId && userData.sessionId && userData.requestId);
  
  // Return true if either basic user data or complete patient data is present
  return hasRequiredFields || (hasRequiredFields && hasPatientFields);
};

/**
 * Get current time formatted
 * @returns {string} - Current time in HH:MM format
 */
export const getCurrentTime = () => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Function to convert Western numerals to Arabic numerals
export const convertToArabicNumerals = (number) => {
  const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return number
    .toString()
    .replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};
